import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PageShortcutProvider } from './context/PageShortcutContext';
import { FYProvider } from './context/FYContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import Homepage from './pages/Homepage';
import Dashboard from './pages/Dashboard';
import Income from './pages/Income';
import Expense from './pages/Expense';
import Sales from './pages/Sales';
import Ledger from './pages/Ledger';
import Recurring from './pages/Recurring';
import Reports from './pages/Reports';
import Purchase from './pages/Purchase';
import Inventory from './pages/Inventory';
import AuditTrail from './pages/AuditTrail';
import Users from './pages/Users';
import DeletedRecords from './pages/DeletedRecords';
import AllEntries from './pages/AllEntries';
import GST from './pages/GST';
import Master from './pages/Master';
import ProductionPage from './pages/Production';
import Notes from './pages/Notes';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user && !user.must_change_password) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PasswordChangeRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      {/* Change Password Route */}
      <Route path="/change-password" element={
        <PasswordChangeRoute>
          <ChangePassword />
        </PasswordChangeRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      <Route path="/income" element={
        <ProtectedRoute>
          <Income />
        </ProtectedRoute>
      } />

      <Route path="/expense" element={
        <ProtectedRoute>
          <Expense />
        </ProtectedRoute>
      } />

      <Route path="/sales" element={
        <ProtectedRoute>
          <Sales />
        </ProtectedRoute>
      } />

      <Route path="/purchase" element={
        <ProtectedRoute>
          <Purchase />
        </ProtectedRoute>
      } />

      <Route path="/inventory" element={
        <ProtectedRoute>
          <Inventory />
        </ProtectedRoute>
      } />

      <Route path="/production" element={
        <ProtectedRoute>
          <ProductionPage />
        </ProtectedRoute>
      } />

      <Route path="/notes" element={<Navigate to="/sales/credit-notes" replace />} />
      <Route path="/sales/credit-notes" element={
        <ProtectedRoute>
          <Notes module="sales" noteType="credit" />
        </ProtectedRoute>
      } />
      <Route path="/purchase/debit-notes" element={
        <ProtectedRoute>
          <Notes module="purchase" noteType="debit" />
        </ProtectedRoute>
      } />

      <Route path="/ledger" element={
        <ProtectedRoute>
          <Ledger />
        </ProtectedRoute>
      } />

      <Route path="/recurring" element={
        <ProtectedRoute>
          <Recurring />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={<Navigate to="/sales" replace />} />

      {/* Admin Only Routes */}
      <Route path="/audit-trail" element={
        <ProtectedRoute adminOnly>
          <AuditTrail />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <Users />
        </ProtectedRoute>
      } />

      <Route path="/deleted-records" element={
        <ProtectedRoute adminOnly>
          <DeletedRecords />
        </ProtectedRoute>
      } />

      <Route path="/all-entries" element={
        <ProtectedRoute>
          <AllEntries />
        </ProtectedRoute>
      } />

      <Route path="/gst" element={
        <ProtectedRoute>
          <GST />
        </ProtectedRoute>
      } />

      <Route path="/master" element={
        <ProtectedRoute>
          <Master />
        </ProtectedRoute>
      } />


      {/* Default: Homepage */}
      <Route path="/" element={
        <ProtectedRoute>
          <Homepage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <FYProvider>
          <PageShortcutProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors toastOptions={{ style: { marginTop: '3.5rem' } }} />
          </PageShortcutProvider>
          </FYProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
